import React, { useState, useEffect } from "react";
import BackdropComponent from "../../components/Spinner/Spinner";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";
import { ContentCopy } from "@mui/icons-material";
import SnackBar from "components/SnackBar/SnackBar";
import {
  getInformaticaEmployeeData,
  getOracleIdsPII,
} from "../../services/userServices";
import PropTypes from "prop-types";
import { fontStyles } from "components/Common/commonStyles";
import { useAuth } from "context/AuthContext";
import piiAuditLog from "../../utils/piiAuditLog";

const PiiReportRITMDisplay = (props) => {
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState([false]);
  const [piiData, setPiiData] = useState([]);
  const [oracleIdsData, setOracleIdsData] = useState([]);
  const [cppCode, setCppCode] = useState("");
  const [isPrvAccess, setIsPrvAccess] = useState(false);

  let fieldsToFetch = [
    "contact_number",
    "country",
    "npn",
    "street_address_1",
    "street_address_2",
    "street_address_3",
    "city",
    "state",
    "postal_code",
    "work_email",
    "personal_email",
    "national_idenfifier_partial",
    "insurance_license_number",
  ];

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const copyToClipboard = () => {
    const piiTable = document.getElementById("piiTable");
    if (!piiTable) return;
    const headers = Array.from(
      piiTable.querySelectorAll("thead th, thead td"),
    ).map((cell) => cell.innerText.trim());
    const rows = Array.from(piiTable.querySelectorAll("tbody tr")).map((row) =>
      Array.from(row.querySelectorAll("td, th")).map((cell) =>
        cell.innerText.trim(),
      ),
    );
    let tableText = "";
    if (headers.length) {
      tableText += headers.join("\t") + "\n";
    }
    rows.forEach((row) => {
      tableText += row.join("\t") + "\n";
    });

    navigator.clipboard.writeText(tableText.trim());
    setSnack({
      open: true,
      message: "Copied!",
      severity: "success",
    });
  };

  const contentFontStyle = {
    ...fontStyles,
    fontSize: 14,
    letterSpacing: 0.1,
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  // ASTT-7304: PII Access Logging — tracks which access tier actually gated
  // the fields fetched below, since fieldsToFetch alone doesn't say why.
  let resolvedUserGroup = "PII_Group_Med";

  const updateFieldsToFetch = (resultData) => {
    const { ProjectCodeFull, NationalIdentifier, DateOfBirth, Gender } =
      resultData;
    setCppCode(ProjectCodeFull);

    if (hasPermission("PII_Group_FULL")) {
      resolvedUserGroup = "PII_Group_FULL";
      if (NationalIdentifier && DateOfBirth && Gender) {
        fieldsToFetch = ["all_fields"];
      } else {
        if (NationalIdentifier) fieldsToFetch.push("national_identifier");
        if (DateOfBirth) fieldsToFetch.push("date_of_birth");
        if (Gender) fieldsToFetch.push("gender");
      }
    } else if (hasPermission("ISU_PRV_Access")) {
      resolvedUserGroup = "ISU_PRV_Access";
      fieldsToFetch = ["date_of_birth"];
      setIsPrvAccess(true);
    }
  };

  const fetchInformaticaEmployeeData = async (
    oracleIds,
    isPrvAccess,
    licenseNpnEnabled,
  ) => {
    const obj = {
      oracle_id: oracleIds,
      show_pii_field: fieldsToFetch.join(","),
    };

    const piiData = await getInformaticaEmployeeData(obj, isPrvAccess);

    if (piiData.status === true) {
      setPiiData(piiData.data);

      // ASTT-7304: PII Access Logging. Only log when PII was actually shown.
      // Note: the UI displays "date_of_birth" as Year-of-Birth only for
      // ISU_PRV_Access; the field requested from Informatica doesn't change,
      // only how it's rendered, so we relabel it here for the audit record.
      //
      // NPN / InsuranceLicenseNumber come from a separate local query
      // (usp_web_GetOracleIdsAndPIIFieldsByIDMTicketNumber, via oracleIdsData)
      // rather than from Informatica, and that SP forces both to 'NA' unless
      // OBTManagementInfo.LicenseNPN = 1 for the project — confirmed by
      // reading the SP directly. licenseNpnEnabled reflects that flag.
      if (piiData.data?.employee_datalist?.length > 0) {
        const shownFields = fieldsToFetch.map((f) =>
          f === "date_of_birth" && resolvedUserGroup === "ISU_PRV_Access"
            ? "date_of_birth_year_only"
            : f,
        );

        if (licenseNpnEnabled) {
          shownFields.push("NPN", "InsuranceLicenseNumber");
        }

        piiAuditLog({
          oracleIds,
          userGroup: resolvedUserGroup,
          sourceModule: "PIIReportByRITM",
          moduleReference: props.ticketNumber,
          piiFieldsShown: shownFields.join(","),
          piiFieldsMasked: licenseNpnEnabled
            ? ""
            : "NPN,InsuranceLicenseNumber",
        });
      }
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        const resultData = await getOracleIdsPII(props.ticketNumber);

        if (cancelled) return;

        window.localStorage.removeItem("OBTPII");

        if (resultData.data.length === 0) {
          if (!cancelled) {
            setLoading(false);
            setSnack({
              open: true,
              message: "No Record found",
              severity: "info",
            });
          }
          return;
        }

        if (
          resultData.status === true &&
          resultData.data[0].OracleIds != null
        ) {
          if (!cancelled) {
            setOracleIdsData(resultData.data);
            updateFieldsToFetch(resultData.data[0]);
          }

          // ASTT-7304: PII Access Logging. LicenseNPN is a project-level flag
          // (from OBTManagementInfo via the SP), so the first row is
          // representative of the whole roster/RITM.
          const licenseNpnEnabled =
            resultData.data[0]?.LicenseNPN === true ||
            resultData.data[0]?.LicenseNPN === 1;

          await fetchInformaticaEmployeeData(
            resultData.data[0].OracleIds,
            isPrvAccess,
            licenseNpnEnabled,
          );

          if (cancelled) return;
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch PII data:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [isPrvAccess]);

  return (
    <div>
      {loading ? <BackdropComponent /> : null}
      <Box sx={fontStyles}>
        <Paper>
          <Grid container>
            <Grid item xs={12}>
              <CustomButton
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: "#3299FF",
                  color: "#FFF",
                  borderRadius: 0,
                }}
                endIcon={<ContentCopy />}
                onClick={copyToClipboard}
              >
                Copy to Clipboard
              </CustomButton>
            </Grid>
            <Grid item xs={12}>
              <TableContainer
                sx={{ height: "calc(100vh - 35px)", overflowY: "auto" }}
              >
                <Table id="piiTable" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TextLabel text="Oracle ID" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Last Name" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="First Name" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Middle Name" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Site" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Location ID" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="CPP Code" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Client/Program" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="New CPP Code" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="New Client/Program Name" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Department" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Job Title" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Work Arrangement" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Taleo ID" />
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={isPrvAccess ? "Year of Birth" : "Date of Birth"}
                        />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Personal Email Address" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Contact Number" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Street Address" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Street Address 2" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="City" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="State" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Country" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Postal Code" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Gender" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="SSN/International ID" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="Insurance License Number" />
                      </TableCell>
                      <TableCell>
                        <TextLabel text="NPN" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {piiData?.employee_datalist?.map((row, i) => {
                      const oracleRow = oracleIdsData?.find(
                        (oracleItem) =>
                          oracleItem?.OracleId &&
                          String(oracleItem?.OracleId) ==
                            String(row?.oracle_id),
                      );
                      const rowKey = `${row.oracle_id}-${i}`;
                      return (
                        <TableRow key={rowKey}>
                          <TableCell
                            sx={contentFontStyle}
                            component="th"
                            scope="row"
                            align="right"
                          >
                            {row?.oracle_id}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.last_name}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.first_name}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.middle_name}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.site}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {oracleRow?.GLLocCd ?? "NA"}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {cppCode}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.client_or_program}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {oracleRow?.newClientProgramCodeFull ?? "NA"}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {oracleRow?.newClientProgramName ?? "NA"}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.department}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {oracleRow?.JobTitle ?? "NA"}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.work_arrangement}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.job_information?.taleo_id}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.date_of_birth}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.personal_email}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.contact_number}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.street_address_1}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.street_address_2}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.city}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.state}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.country}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.postal_code}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.gender}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {row?.personal_information?.national_identifier}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {oracleRow?.InsuranceLicenseNumber ?? "NA"}
                          </TableCell>
                          <TableCell sx={contentFontStyle} align="=right">
                            {oracleRow?.NPN ?? "NA"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </div>
  );
};

PiiReportRITMDisplay.propTypes = {
  ticketNumber: PropTypes.string,
};

export default PiiReportRITMDisplay;
